import { useEffect, useRef, useState } from "react";
import type { DocumentNode } from "graphql";
import { useQuery } from "@apollo/client/react";

/**
 * Read every row of a paginated list query, not just the first page.
 *
 * The admin list pages — Library, Courses, the Data tab's video panel — search, filter and count
 * over the array they hold. None of them passed a `limit`, so they got `normalizePagination`'s
 * default of 50 and every one of those operations silently described the first 50 rows instead of
 * the collection: 620 videos rendered as 50, and the Courses tab counts read "50 published" the
 * same way a broken clock reads noon.
 *
 * Paging the UI would not fix that. A "load more" grid still searches only what has loaded, and the
 * counts still describe a prefix. These pages are built on holding the whole set, so this fetches
 * the whole set — one page at a time, accumulating, until a short page says there are no more.
 *
 * Deliberately not solved by raising `MAX_LIMIT`. That cap is the public API's guard against a
 * single request asking for everything, and it applies to learner-facing queries too; loosening it
 * for the benefit of three admin screens spends a protection that is doing its job. Paging under
 * the cap costs a few extra round trips on a page an operator opens occasionally.
 *
 * Requires an appending merge policy on the field — see `offsetLimitPagination` in `lib/apollo`.
 * Without one the cache overwrites on each page and this settles on whichever arrived last.
 *
 * The inbox wants the same walk with the opposite presentation — the newest conversations on
 * screen immediately, older ones filling in behind them — so it reads `initialLoading` rather than
 * `loading` and asks for a smaller `pageSize`. Same accumulation underneath; only the point at
 * which the caller starts rendering differs.
 */

/**
 * `MAX_LIMIT` in `common/database/repositories/_pagination.ts`. Asking for more is clamped, and a
 * clamped page shorter than requested reads as "no more rows" and stops the walk early.
 *
 * Exported because anything issuing the *same* query outside this hook has to send the same
 * variables. The merge policy keys only on the filter, so a sibling query with a different `limit`
 * writes its rows over the walked list at whatever offset it claims.
 */
export const PAGE_SIZE = 200;

/** Stop rather than page forever if a server ever keeps returning full pages. */
const MAX_PAGES = 50;

interface UseAllRowsResult<TRow> {
    rows: TRow[];
    /**
     * True until the collection is whole — the first page and the walk behind it.
     *
     * What a list page wants when it searches or counts over `rows`, because a partial answer to
     * "how many are published" is a wrong one.
     */
    loading: boolean;
    /**
     * True only until the *first* page arrives.
     *
     * What a page wants when it can usefully render a prefix — the inbox shows the newest
     * conversations immediately and lets older ones fill in behind them, rather than holding a
     * spinner over a list that is already useful.
     */
    initialLoading: boolean;
    /** The walk after the first page is still running. Pairs with `initialLoading`. */
    pagingMore: boolean;
    refetch: () => Promise<unknown>;
}

interface UseAllRowsOptions {
    variables?: Record<string, unknown>;
    /** Passed through to Apollo. A skipped query pages nothing — there is nothing to page. */
    skip?: boolean;
    /**
     * Rows per request, when the default is the wrong trade.
     *
     * A grid of cards wants few large pages. The inbox wants the opposite: each chat carries its
     * messages, so a smaller page puts the newest conversations on screen sooner and lets the rest
     * arrive behind them. Capped at `PAGE_SIZE` regardless — the server clamps anything larger,
     * and a clamped page would read as the end of the collection.
     */
    pageSize?: number;
}

export const useAllRows = <TRow>(
    document: DocumentNode,
    select: (data: unknown) => TRow[] | undefined,
    { variables = {}, skip = false, pageSize = PAGE_SIZE }: UseAllRowsOptions = {},
): UseAllRowsResult<TRow> => {
    const size = Math.min(pageSize, PAGE_SIZE);
    const { data, loading, refetch, fetchMore } = useQuery(document, {
        variables: { ...variables, limit: size, offset: 0 },
        skip,
    });

    // Whether the pages after the first are still arriving. Tracked here rather than read from
    // Apollo so a caller can hold its spinner until the collection is whole — rendering
    // "50 courses" mid-walk is the bug this hook exists to remove.
    const [
        paging,
        setPaging,
    ] = useState(false);

    const rows = select(data) ?? [];

    /*
     * The walk reads the row count and the selector through refs, and neither is a dependency.
     *
     * They change on every merged page by design. As dependencies they re-ran the effect mid-walk,
     * and the cleanup cancelled the loop that was still running — which left `paging` true forever
     * once the final short page made the count stop being a multiple of the page size. The walk has
     * to outlive the renders its own results cause.
     */
    const rowCount = rows.length;
    const rowCountRef = useRef(0);
    rowCountRef.current = rowCount;
    const selectRef = useRef(select);
    selectRef.current = select;
    const variablesRef = useRef(variables);
    variablesRef.current = variables;
    // `fetchMore` too, and this one is load-bearing rather than tidy: Apollo returns a new
    // function identity on each render, so as a dependency it re-ran the effect the moment the
    // first page merged. The cleanup then set `cancelled`, the walk returned before clearing
    // `paging`, and the list spun forever having fetched exactly one extra page.
    const fetchMoreRef = useRef(fetchMore);
    fetchMoreRef.current = fetchMore;

    // One walk per set of variables. Changing the organization filter starts a new list and so
    // needs a new walk; a re-render of the same list must not start a second one.
    const key = JSON.stringify(variables);
    const walkedKey = useRef<string | null>(null);

    /*
     * Cancellation is tied to unmount, not to the effect's lifecycle.
     *
     * The obvious shape — a `cancelled` flag closed over by the effect and set in its cleanup —
     * cannot work here, because Apollo flips `loading` back to `true` for the duration of every
     * `fetchMore` (with or without `notifyOnNetworkStatusChange`). Any dependency that tracks the
     * query therefore changes mid-walk, the cleanup runs, and the walk aborts after exactly one
     * extra page while `paging` is still set — a list that spins forever holding 400 of 620 rows.
     *
     * So the effect is free to re-run: `walkedKey` makes a second run a no-op, and only leaving the
     * page stops the walk.
     */
    const unmounted = useRef(false);
    useEffect(
        () => () => {
            unmounted.current = true;
        },
        [],
    );

    useEffect(() => {
        if (skip || loading) return;
        if (walkedKey.current === key) return;

        const first = rowCountRef.current;
        // A partial first page is already the whole collection, and an empty one has nothing to
        // follow. Neither is marked as walked: the query may simply not have resolved yet.
        if (first === 0 || first % size !== 0) return;

        walkedKey.current = key;
        setPaging(true);
        void (async () => {
            try {
                let offset = first;
                for (let page = 0; page < MAX_PAGES; page += 1) {
                    const result = await fetchMoreRef.current({
                        variables: { ...variablesRef.current, limit: size, offset },
                    });
                    if (unmounted.current) return;
                    const fetched = selectRef.current(result.data)?.length ?? 0;
                    if (fetched < size) break;
                    offset += fetched;
                }
            } finally {
                // Including on a failed page. A half-read list is worth showing; a spinner that
                // never stops is not, and the rows already merged are still correct.
                if (!unmounted.current) setPaging(false);
            }
        })();
    }, [
        skip,
        loading,
        key,
        rowCount,
        size,
    ]);

    return { rows, loading: loading || paging, initialLoading: loading, pagingMore: paging, refetch };
};
